'use client'

import { Download } from 'lucide-react'
import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { trackEvent } from '@/lib/analytics'

type PdfExportButtonProps = Omit<ButtonProps, 'onClick' | 'variant' | 'children'> & {
  /** Optional override of the button label. */
  label?: string
}

/**
 * "Save as PDF" button for the lawn products guide.
 *
 * Cheapest-possible PDF export: relies on the browser's native print dialog
 * (Ctrl/Cmd+P → "Save as PDF") together with the lawn-products-guide print
 * stylesheet in `app/globals.css`. No PDF libraries, no canvas rasterizing,
 * no server roundtrip.
 *
 * Styled to match the page's other secondary CTAs (outline button with a
 * green border and green-700 label). Fires the
 * `lawn_guide_pdf_export_clicked` analytics event before invoking
 * `window.print()`.
 */
export function PdfExportButton({
  className,
  label = 'Save as PDF',
  ...buttonProps
}: PdfExportButtonProps) {
  const handleClick = () => {
    trackEvent('lawn_guide_pdf_export_clicked')
    if (typeof window !== 'undefined') {
      window.print()
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleClick}
      className={cn(
        'border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800 hover:border-green-700 font-semibold print:hidden',
        className,
      )}
      {...buttonProps}
    >
      <Download className="w-4 h-4" aria-hidden="true" />
      {label}
    </Button>
  )
}

export default PdfExportButton
